import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import styles from '@/modules/general-notes/styles.less';
import NoteDetails from '@/modules/general-notes/components/NoteDetails';
import NoteForm from '@/modules/general-notes/components/NoteForm';
import ConfirmModal from '@/common/containers/Modals/ConfirmModal';
import cx from 'classnames';
import { Storage } from '@/common/utils';

const getExpandedStorageKey = (noteId) => `general-note-expanded-${noteId}`;

const Note = ({
  id,
  title,
  expanded,
  items = [],
  onSave,
  onDelete,
  onToggleItem,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExpanded, setIsExpanded] = useState(() => {
    const savedValue = Storage.get(getExpandedStorageKey(id));
    return typeof savedValue === 'boolean' ? savedValue : expanded;
  });

  useEffect(() => {
    Storage.set(getExpandedStorageKey(id), isExpanded);
  }, [id, isExpanded]);

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  if (isSaving) {
    return (
      <div className={styles.note}>
        <div className={cx(styles.noteContainer, styles.noteContainerForm)}>
          <div className={styles.noList}>
            <span>{t('Loading...')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (isEditing) {
    return (
      <NoteForm
        title={title}
        items={items}
        onCancel={() => setIsEditing(false)}
        onSave={async (payload) => {
          try {
            setIsSaving(true);
            if (onSave) {
              await onSave({
                id,
                originalTitle: title,
                originalItems: items,
                ...payload,
              });
            }
            setIsEditing(false);
          } finally {
            setIsSaving(false);
          }
        }}
      />
    );
  }

  return (
    <div className={styles.note}>
      <div className={styles.noteContainer}>
        <div className={styles.noteHeader}>
          <span className={styles.noteHeaderTitle}>{title}</span>
          <div className={styles.noteHeaderActions}>
            <Button
              startIcon={isExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
              color={isExpanded ? 'secondary' : 'success'}
              onClick={toggleExpanded}
              sx={{ visibility: items.length ? 'visible' : 'hidden' }}
              size="medium"
            >
              {isExpanded ? t('Hide details') : t('Unhide details')}
            </Button>
            <Button
              color="success"
              startIcon={<EditIcon />}
              onClick={() => setIsEditing(true)}
              size="medium"
            >
              {t('Edit')}
            </Button>
          </div>
        </div>
        {isExpanded && (
          <NoteDetails
            items={items}
            onToggle={(item, index, checked) =>
              onToggleItem && onToggleItem(id, item.id, checked)
            }
          />
        )}
      </div>
      <ConfirmModal
        confirmText={t('Delete')}
        title={t('Delete note?')}
        description={t(
          'Are you sure you want to delete this note? This action can’t be undone.'
        )}
        type="delete"
      >
        {(confirm) => (
          <IconButton
            className={styles.noteRemove}
            size="small"
            onClick={confirm(() => onDelete && onDelete(id))}
          >
            <DeleteIcon fontSize="inherit" />
          </IconButton>
        )}
      </ConfirmModal>
    </div>
  );
};

export default Note;
